import { motion, AnimatePresence } from 'framer-motion';
import { X, Palette, Layout, Sparkles, RotateCcw } from 'lucide-react';
import { PresentationSlide, ThemePreset, SlideAnimation, SlideTransition } from '../../types';
import { SLIDE_LAYOUTS, SLIDE_TRANSITIONS, ELEMENT_ANIMATIONS } from '../../data/themePresets';
import ColorPicker from './ColorPicker';

interface SlideSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  slide: PresentationSlide;
  slideIndex: number;
  globalTheme: ThemePreset;
  onUpdate: (slideIndex: number, updates: Partial<PresentationSlide>) => void;
  isDark: boolean;
}

export default function SlideSettingsPanel({
  isOpen,
  onClose,
  slide,
  slideIndex,
  globalTheme,
  onUpdate,
  isDark,
}: SlideSettingsPanelProps) {
  const handleBackgroundChange = (color: string) => {
    onUpdate(slideIndex, { backgroundColor: color });
  };

  const handleLayoutChange = (layout: PresentationSlide['layout']) => {
    onUpdate(slideIndex, { layout });
  };

  const handleTransitionChange = (transition: SlideTransition) => {
    onUpdate(slideIndex, { transition });
  };

  const handleAnimationChange = (animation: SlideAnimation) => {
    onUpdate(slideIndex, { animation });
  };

  const handleResetToGlobal = () => {
    onUpdate(slideIndex, {
      backgroundColor: undefined,
      layout: undefined,
      transition: undefined,
      animation: undefined,
    });
  };

  const hasCustomSettings = slide.backgroundColor || slide.layout || slide.transition || slide.animation;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed right-0 top-0 bottom-0 w-80 z-50 shadow-2xl ${
              isDark ? 'bg-dark-800' : 'bg-white'
            }`}
          >
            {/* Header */}
            <div className={`h-16 border-b flex items-center justify-between px-4 ${
              isDark ? 'border-dark-700' : 'border-slate-200'
            }`}>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Slide {slideIndex + 1} Settings
                </h3>
                <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Customize this slide
                </p>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-6 overflow-y-auto h-[calc(100%-64px)]">
              {/* Reset Button */}
              {hasCustomSettings && (
                <button
                  onClick={handleResetToGlobal}
                  className={`w-full py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                    isDark
                      ? 'bg-dark-700 text-text-secondary hover:bg-dark-600'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Global Settings
                </button>
              )}

              {/* Background Color */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Palette className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Background Color
                  </span>
                </div>
                <ColorPicker
                  value={slide.backgroundColor || globalTheme.colors.background}
                  onChange={handleBackgroundChange}
                  isDark={isDark}
                  themeColors={globalTheme.colors}
                />
                {slide.backgroundColor && (
                  <p className="text-xs text-purple-500 mt-2">
                    Custom color applied
                  </p>
                )}
              </div>

              {/* Layout */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Layout className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Slide Layout
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {SLIDE_LAYOUTS.slice(0, 8).map((layout) => (
                    <button
                      key={layout.id}
                      onClick={() => handleLayoutChange(layout.id as PresentationSlide['layout'])}
                      className={`p-3 rounded-xl text-left transition-all ${
                        (slide.layout || 'default') === layout.id
                          ? 'bg-purple-500 text-white'
                          : isDark
                            ? 'bg-dark-700 text-text-secondary hover:bg-dark-600'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <p className="text-xs font-medium truncate">{layout.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Transition Override */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Transition Override
                  </span>
                </div>
                <select
                  value={slide.transition || ''}
                  onChange={(e) => handleTransitionChange(e.target.value as SlideTransition)}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 text-white'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                >
                  <option value="">Use Global Transition</option>
                  {SLIDE_TRANSITIONS.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} - {t.description}</option>
                  ))}
                </select>
              </div>

              {/* Animation Override */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Element Animation Override
                  </span>
                </div>
                <select
                  value={slide.animation || ''}
                  onChange={(e) => handleAnimationChange(e.target.value as SlideAnimation)}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 text-white'
                      : 'bg-white border-slate-200 text-slate-900'
                  }`}
                >
                  <option value="">Use Global Animation</option>
                  {ELEMENT_ANIMATIONS.map((a) => (
                    <option key={a.id} value={a.id}>{a.name} - {a.description}</option>
                  ))}
                </select>
              </div>

              {/* Slide Notes */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Presenter Notes
                  </span>
                </div>
                <textarea
                  value={slide.notes || ''}
                  onChange={(e) => onUpdate(slideIndex, { notes: e.target.value })}
                  placeholder="Add notes for this slide..."
                  rows={4}
                  className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 text-white placeholder:text-text-muted'
                      : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
                  }`}
                />
              </div>

              {/* Info Box */}
              <div className={`p-3 rounded-xl text-xs ${
                isDark ? 'bg-dark-700 text-text-muted' : 'bg-slate-50 text-slate-500'
              }`}>
                <p className="font-medium mb-1">Per-Slide Customization</p>
                <p>
                  Changes here only affect this slide. Use global settings in the left panel for presentation-wide changes.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
